import { Link, useParams } from 'react-router-dom';
import henceforthApi from '../../utils/henceforthApi';
import { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../../context/Provider';
import Swal from 'sweetalert2';
import Spinner from '../../components/BootstrapCompo';
import PaginationLayout from '../../components/PaginationLayout';

interface ProductType {
    _id: string;
    name: string;
    is_active: boolean;
    created_at: number;
    updated_at: number;
}

interface ProductTypeState {
    data: ProductType[];
    total_count: number;
    page: number;
    limit: number;
}

const ProductType = () => {
    const { authState, loading, setLoading } = useContext(GlobalContext);
    const { page } = useParams();
    const currentPage = Number(page) || 1;

    const [state, setState] = useState<ProductTypeState>({
        data: [],
        total_count: 0,
        page: currentPage,
        limit: 10,
    });

    const [search, setSearch] = useState('');
    const [addModal, setAddModal] = useState(false);
    const [newTypeName, setNewTypeName] = useState('');
    const [addLoading, setAddLoading] = useState(false);
    const [nameError, setNameError] = useState('');

    const fetchProductTypes = async (pg = currentPage, searchVal = search) => {
        setLoading(true);
        try {
            const res = await henceforthApi.ProductTypes.list(pg, 10, searchVal, true);

            console.log('Full response:', res);  // Debug line
            console.log('Response data:', res.data);  // Debug line

            const apiData = res.data;

            setState({
                data: Array.isArray(apiData.data) ? apiData.data : [],
                total_count: apiData.total_count || 0,
                page: apiData.page || pg,
                limit: apiData.limit || 10,
            });

            console.log('Set state data:', apiData.data);  // Debug line
        } catch (error) {
            console.error('Error fetching product types:', error);
            setState({
                data: [],
                total_count: 0,
                page: 1,
                limit: 10,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProductTypes(currentPage, search);
    }, [currentPage, authState?.lang]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        fetchProductTypes(1, e.target.value);
    };

    const handleAdd = async () => {
        if (!newTypeName.trim()) {
            setNameError('Product type name is required.');
            return;
        }
        setNameError('');
        setAddLoading(true);
        try {
            await henceforthApi.ProductTypes.create(newTypeName.trim(), true);
            setNewTypeName('');
            setAddModal(false);
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Product type added successfully!',
                timer: 1500,
                showConfirmButton: false,
            });
            fetchProductTypes(currentPage, search);
        } catch (error: any) {
            const msg = error?.response?.body?.message || 'Failed to add product type.';
            Swal.fire({ icon: 'error', title: 'Error', text: msg });
        } finally {
            setAddLoading(false);
        }
    };

    const handleDelete = (id: string, name: string) => {
        Swal.fire({
            title: 'Are you sure?',
            text: `Delete product type "${name}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await henceforthApi.ProductTypes.delete(id);
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Product type has been deleted.',
                        timer: 1500,
                        showConfirmButton: false,
                    });
                    fetchProductTypes(currentPage, search);
                } catch (error: any) {
                    Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete product type.' });
                }
            }
        });
    };

    const handleToggleActive = async (id: string) => {
        try {
            await henceforthApi.ProductTypes.toggleActive(id);
            fetchProductTypes(currentPage, search);
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update status.' });
        }
    };
    const onChangePagination = (page: number) => {
    window.location.href = `/product-types/${page}`;
};

    const totalPages = Math.ceil(state.total_count / state.limit);

    return (
        <>
            {/* Breadcrumb */}
            <section className="breadcrum-box">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12">
                            <h2 className="fw-semibold">Product Types</h2>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb m-0">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item active fw-bold">Product Types</li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            {/* Page content */}
            {loading ? <div className='d-flex justify-content-center py-5'>
                <Spinner />
            </div> :
                <div className="page-spacing">
                    <section>
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-12">
                                    <div className="common-card">
                                        {/* Card Header */}
                                        <div className="common-card-title d-flex align-items-center justify-content-between flex-wrap gap-2">
                                            <h5 className="mb-0">Product Type List</h5>
                                            <button
                                                className="btn btn-primary d-flex align-items-center gap-2"
                                                onClick={() => { setAddModal(true); setNameError(''); setNewTypeName(''); }}
                                            >
                                                <i className="fa fa-plus"></i> Add Product Type
                                            </button>
                                        </div>

                                        {/* Search */}
                                        <div className="common-card-content">
                                            <div className="row mb-3">
                                                <div className="col-md-4">
                                                    <div className="input-group">
                                                        <span className="input-group-text bg-white">
                                                            <i className="fa fa-search text-muted"></i>
                                                        </span>
                                                        <input
                                                            type="text"
                                                            className="form-control border-start-0"
                                                            placeholder="Search product types..."
                                                            value={search}
                                                            onChange={handleSearch}
                                                        />
                                                    </div>
                                                </div>
                                            </div>


                                            <div className="data-list-table table-responsive">
                                                <table className="table table-striped align-middle">
                                                    <thead>
                                                        <tr>
                                                            <th>S.No.</th>
                                                            <th>Name</th>
                                                            <th>Status</th>
                                                            <th>Created At</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Array.isArray(state.data) && state.data.length > 0 ? (
                                                            state.data.map((item, index) => (
                                                                <tr key={item._id} style={{ color: '#000' }}>
                                                                    <td>{(currentPage - 1) * state.limit + index + 1}</td>
                                                                    <td className="text-capitalize fw-semibold">{item.name.replaceAll('_', ' ')}</td>
                                                                    <td>
                                                                        <div
                                                                            className="form-check form-switch"
                                                                            title={item.is_active ? 'Active' : 'Inactive'}
                                                                            style={{ cursor: 'pointer' }}
                                                                        >
                                                                            <input
                                                                                className="form-check-input"
                                                                                type="checkbox"
                                                                                role="switch"
                                                                                checked={item.is_active}
                                                                                onChange={() => handleToggleActive(item._id)}
                                                                                style={{ cursor: 'pointer' }}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        {item.created_at
                                                                            ? new Date(item.created_at).toLocaleDateString('en-US', {
                                                                                year: 'numeric', month: 'short', day: 'numeric'
                                                                            })
                                                                            : 'N/A'}
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                                                            onClick={() => handleDelete(item._id, item.name)}
                                                                            title="Delete"
                                                                        >
                                                                            <i className="fa fa-trash"></i> Delete
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={5} className="text-center py-4 text-muted">
                                                                    <i className="fa fa-inbox fs-3 d-block mb-2"></i>
                                                                    No product types found
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>


                                            {/* Pagination */}
                                            {/* {totalPages > 1 && (
                                                <nav className="mt-3">
                                                    <ul className="pagination justify-content-end mb-0">
                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                            <Link className="page-link" to={`/product-types/${currentPage - 1}`}>
                                                                <i className="fa fa-chevron-left"></i>
                                                            </Link>
                                                        </li>
                                                        {[...Array(totalPages)].map((_, i) => (
                                                            <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                                <Link className="page-link" to={`/product-types/${i + 1}`}>{i + 1}</Link>
                                                            </li>
                                                        ))}
                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                            <Link className="page-link" to={`/product-types/${currentPage + 1}`}>
                                                                <i className="fa fa-chevron-right"></i>
                                                            </Link>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            )} */}
                                            <div className='dashboad-pagination-box'>
                                                <PaginationLayout
                                                    count={state?.total_count as number}
                                                    data={state?.data}
                                                    page={currentPage}
                                                    limit={state?.limit}
                                                    onPageChange={(val: any) => onChangePagination(val)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            }

            {/* Add Product Type Modal */}
            {addModal && (
                <>
                    <div
                        className="modal fade show d-block"
                        tabIndex={-1}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold">
                                        <i className="fa fa-plus-circle me-2 text-primary"></i>Add Product Type
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setAddModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">
                                            Product Type Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${nameError ? 'is-invalid' : ''}`}
                                            placeholder="e.g. Electronics, Clothing..."
                                            value={newTypeName}
                                            onChange={(e) => {
                                                setNewTypeName(e.target.value);
                                                if (e.target.value.trim()) setNameError('');
                                            }}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                                            autoFocus
                                        />
                                        {nameError && (
                                            <div className="invalid-feedback">{nameError}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setAddModal(false)}
                                        disabled={addLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary d-flex align-items-center gap-2"
                                        onClick={handleAdd}
                                        disabled={addLoading}
                                    >
                                        {addLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm"></span> Saving...
                                            </>
                                        ) : (
                                            <><i className="fa fa-save"></i> Save</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}
        </>
    );
};

export default ProductType;